import { useEffect, useState } from 'react';
import { HiOutlinePencil } from 'react-icons/hi';

import {
  Button,
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  StatusField
} from '@amfintech/react-admin-ui';
import { DynamicForm, ResourceTableConfig, useResourceAccess } from '@amfintech/refine-react-admin';
import { useOne } from '@refinedev/core';
import { useForm } from '@refinedev/react-hook-form';

import { ConsentStatus, CreationStatus, ProfileConsent } from '~/api';
import { resourceNames } from '~/resources/resource-names';

const formatStatus = (status: ConsentStatus) => {
  switch (status) {
    case ConsentStatus.Active:
      return 'success';
    case ConsentStatus.Revoked:
      return 'danger';
    default:
      return 'pending';
  }
};

export const ProfileConsentPage = ({
  createTableComponent,
  profileId,
}: {
  createTableComponent: <T extends object>(props: ResourceTableConfig<T>) => () => React.ReactNode;
  profileId: string;
}) => {

  const TableComponent = createTableComponent<ProfileConsent>({
    resource: resourceNames.profileConsent,
    fields: [
      'id',
      'consentType',
      'approvedEmail',
      'updatedAt',
      { issuerProfile: ['id', 'fullName', 'companyName', 'idNo', 'email', 'type'] },
      { recipientProfile: ['id', 'fullName', 'companyName', 'idNo', 'email', 'type'] },
      'status',
      'attachmentId',
    ],
    columns: () => [
      {
        id: 'issuerProfile.companyName',
        header: 'Consent To',
        accessorKey: 'issuerProfile.companyName',
      },
      {
        id: 'issuerProfile.idNo',
        header: 'Consent ID',
        accessorKey: 'issuerProfile.idNo',
      },
      {
        id: 'email',
        header: 'Dedicated Email Address',
        accessorKey: 'approvedEmail',
      },
      {
        id: 'consentType',
        header: 'Consent Type',
        accessorKey: 'consentType',
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        cell: (data) => {
          const status = data.cell.getValue<ConsentStatus>();
          return <StatusField status={formatStatus(status)} label={status} />;
        },
      },
      {
        id: 'attachment',
        header: 'Attachment',
        accessorKey: 'attachment',
        cell: (profileConsent) => {
          const attachmentId = profileConsent.row.original.attachmentId;
          const [shouldFetch, setShouldFetch] = useState(false);

          const { data, isLoading } = useOne({
            resource: resourceNames.document,
            id: attachmentId ?? undefined,
            meta: { fields: ['encodedData'] },
            queryOptions: {
              enabled: shouldFetch,
            },
          });

          const handleDownload = () => {
            setShouldFetch(true);
          };

          useEffect(() => {
            if (data && shouldFetch && attachmentId) {
              const file = data.data.encodedData;
              const link = document.createElement('a');
              link.href = file;
              link.download = `profile-consent-${attachmentId}`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setShouldFetch(false);
            }
          }, [data, shouldFetch, attachmentId]);

          return (
            <>
              {attachmentId ? (
                <Button variant="solid" onClick={handleDownload}>
                  Download Attachment
                </Button>
              ) : (
                <p className="text-sm text-gray-500">No Attachment</p>
              )}
            </>
          );
        },
      },
      {
        id: 'action',
        header: 'Action',
        accessorKey: 'action',
        cell: (data) => {
          const status = data.row.original.status;
          const [isShowEditDialog, setIsShowEditDialog] = useState(false);
          const { canWrite } = useResourceAccess(resourceNames.profile);
          const form = useForm<ProfileConsent>({
            refineCoreProps: {
              resource: resourceNames.profileConsent,
              action: 'edit',
              redirect: false,
              errorNotification: (error) => {
                return {
                  message: error?.message || 'An error occurred',
                  type: 'error',
                };
              },
              onMutationSuccess: () => setIsShowEditDialog(false),
              id: data.row.original.id,
              metaData: {
                fields: ['status'] as any[],
              },
            },
          });

          return (
            <Dialog open={isShowEditDialog} onOpenChange={setIsShowEditDialog}>
              <DialogTrigger asChild>
                <Button disabled={!canWrite} variant={'outline'} size="sm" className="gap-2">
                  <HiOutlinePencil className="w-4 h-4" /> Edit
                </Button>
              </DialogTrigger>
              <DialogPortal>
                <DialogOverlay />
                <DialogContent>
                  <DialogTitle>Edit Consent</DialogTitle>
                  <div className="lg:min-w-full">
                    <div className="py-3">
                      <DynamicForm<ProfileConsent>
                        form={form}
                        defaultValues={{ status }}
                        controls={{
                          components: {
                            status: {
                              type: 'select',
                              config: {
                                label: 'Status',
                                options: [
                                  { label: 'Active', value: ConsentStatus.Active },
                                  { label: 'Revoked', value: ConsentStatus.Revoked },
                                ],
                              },
                            },
                            attachment: {
                              type: 'image-upload',
                              config: {
                                label: 'Attachment',
                                required: false,
                              },
                            },
                          },
                        }}
                        submitText={false}
                        id={'edit-profile-consent-dialog'}
                        inferSections={false}
                        type={'edit'}
                        confirmBeforeSubmit
                        confirmationTitle="Profile Consent Update Confirmation"
                        confirmationDescription="Please confirm that you want to update this Profile Consent."
                      />
                    </div>
                    <div className="flex flex-row-reverse gap-3 pt-2 border-t">
                      <Button
                        type="submit"
                        variant="solid"
                        loading={form.refineCore.mutationResult.isLoading}
                        disabled={form.refineCore.formLoading}
                        form={'edit-profile-consent-dialog'}
                      >
                        Save
                      </Button>
                      <Button
                        onClick={() => setIsShowEditDialog(false)}
                        disabled={
                          form.refineCore.formLoading || form.refineCore.mutationResult.isLoading
                        }
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </DialogPortal>
            </Dialog>
          );
        },
      },
    ],
    filterConfig: {
      permanent: [
        {
          field: 'recipientProfileId' as any,
          operator: 'eq',
          value: profileId,
        },
        {
          field: 'creationStatus',
          operator: 'eq',
          value: CreationStatus.Approved,
        },
      ],
    },
  });

  return <TableComponent />;
};
